/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Dec 18 2023
 **/

import { useState } from "react";

import { Button, Column, Divider, Grid, Radio, Row, Typography } from "@/frameworks/designStuff/components";
import { _T, _P } from "@rfice/language";

import { Avatar } from "@/frameworks/core/components";
import * as S from "./private/emotion";

type Option = "showPreviousChat" | "createNewChat";

export default function IncludeConversation({
	inviteUserList,
	onSubmit,
	onCancel
}: {
	inviteUserList: WebServices.People.List.Data[number]["users"];
	onSubmit: (option: Option) => void;
	onCancel: () => void;
}) {
	const [option, setOption] = useState<Option>("showPreviousChat");

	return (
		<Column css={{ minWidth: "520px" }}>
			<S.ModalBody horizonAlign="center" gap={24}>
				<Row css={{ width: "100%" }} horizonAlign="distribute">
					<Typography typoSize="Head6" typoColor="neutral_80_100">
						{_T("대화 내역 포함")}
					</Typography>
					<Button.Icon name="Close24" filled="transparent" variant="contained" size="medium" />
				</Row>
				<Grid
					gridGap={8}
					row={Math.ceil(inviteUserList.length / 3)}
					repeatSize={{ row: "40px" }}
					css={{ maxHeight: "calc(40px * 3 + 8px * 2)", overflow: "auto" }}
					column={inviteUserList.length > 3 ? 3 : inviteUserList.length}
				>
					{inviteUserList.map((user) => {
						return (
							<Row gap={12} key={user.userId}>
								<Avatar id={user.userId} kind="people_32" useStatus={false} />
								<Typography typoSize="Head6" typoColor="neutral_80_100">
									{user.name}
								</Typography>
							</Row>
						);
					})}
				</Grid>
				<Row>
					<Typography typoColor="neutral_70_100" typoSize="Body2">
						{_P(
							"대화 상대가 새롭게 추가되었습니다.<br />추가한 사용자가 이전 대화 내용을 볼 수 있도록 하시겠습니까?"
						)}
					</Typography>
				</Row>
				<Divider type="line" />
				<Column gap={4}>
					<Radio
						id="showPreviousChat"
						value={"showPreviousChat"}
						checked={option === "showPreviousChat"}
						onChange={() => {
							setOption("showPreviousChat");
						}}
					>
						{_T("이전 대화 기록을 볼 수 있습니다.")}
					</Radio>
					<Radio
						id="createNewChat"
						value={"createNewChat"}
						checked={option === "createNewChat"}
						onChange={() => {
							setOption("createNewChat");
						}}
					>
						{_T("새로운 채팅에 채팅 멤버 추가")}
					</Radio>
				</Column>
			</S.ModalBody>
			<Divider type="line" />
			<Row css={{ width: "100%", padding: "10px 20px" }} horizonAlign="right" gap={8}>
				<Button filled="secondary" onClick={onCancel}>
					{_T("취소")}
				</Button>
				<Button onClick={() => onSubmit(option)}>{_T("적용하기")}</Button>
			</Row>
		</Column>
	);
}
